"use client";

import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> & {
    showTooltip?: boolean;
    tooltipContent?: (value: number) => React.ReactNode;
    options?: string[];
    showSelectedValue?: boolean;
  }
>(({ className, showTooltip = false, tooltipContent, options, showSelectedValue = true, ...props }, ref) => {
  const [showTooltipState, setShowTooltipState] = React.useState(false);
  
  // Initialize with defaultValue if provided, otherwise use value, or default to [0]
  const [internalValue, setInternalValue] = React.useState<number[]>(() => {
    if (props.defaultValue !== undefined) {
      return props.defaultValue as number[];
    }
    if (props.value !== undefined) {
      return props.value as number[];
    }
    return [0];
  });

  // Update internal value when props.value changes
  React.useEffect(() => {
    if (props.value !== undefined) {
      setInternalValue(props.value as number[]);
    }
  }, [props.value]);
  
  // Update internal value when props.defaultValue changes and no value is set
  React.useEffect(() => {
    if (props.defaultValue !== undefined && props.value === undefined) {
      setInternalValue(props.defaultValue as number[]);
    }
  }, [props.defaultValue, props.value]);

  const handleValueChange = (newValue: number[]) => {
    setInternalValue(newValue);
    props.onValueChange?.(newValue);
  };

  const handlePointerDown = () => {
    if (showTooltip) {
      setShowTooltipState(true);
    }
  };

  const handlePointerUp = React.useCallback(() => {
    if (showTooltip) {
      setShowTooltipState(false);
    }
  }, [showTooltip]);

  React.useEffect(() => {
    if (showTooltip) {
      document.addEventListener("pointerup", handlePointerUp);
      return () => {
        document.removeEventListener("pointerup", handlePointerUp);
      };
    }
  }, [showTooltip, handlePointerUp]);

  const renderThumb = (value: number) => {
    const thumb = (
      <SliderPrimitive.Thumb
        className="block h-5 w-5 rounded-full border-2 border-primary bg-background transition-colors focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-ring/40 data-[disabled]:cursor-not-allowed"
        onPointerDown={handlePointerDown}
      />
    );

    if (!showTooltip) return thumb;

    return (
      <TooltipProvider>
        <Tooltip open={showTooltipState}>
          <TooltipTrigger asChild>{thumb}</TooltipTrigger>
          <TooltipContent
            className="px-2 py-1 text-xs"
            sideOffset={8}
            side={props.orientation === "vertical" ? "right" : "top"}
          >
            <p>
              {options 
                ? options[Math.min(value, options.length - 1)] 
                : tooltipContent 
                  ? tooltipContent(value) 
                  : value}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  // Get the currently selected value based on the internal value
  const getSelectedValue = () => {
    if (!options || options.length === 0) return null;
    const index = Math.min(internalValue[0], options.length - 1);
    return options[index];
  };

  return (
    <div className="flex flex-col w-full">
      {showSelectedValue && options && options.length > 0 && (
        <div className="mb-2 text-sm font-medium text-center">
          {getSelectedValue()}
        </div>
      )}
      <SliderPrimitive.Root
        ref={ref}
        className={cn(
          "relative flex w-full touch-none select-none items-center data-[orientation=vertical]:h-full data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col data-[disabled]:opacity-50",
          className,
        )}
        onValueChange={handleValueChange}
        max={options ? options.length - 1 : props.max}
        step={1}
        {...props}
      >
        <SliderPrimitive.Track className="relative grow overflow-hidden rounded-full bg-secondary data-[orientation=horizontal]:h-2 data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-2">
          <SliderPrimitive.Range className="absolute bg-primary data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full" />
        </SliderPrimitive.Track>
        {internalValue?.map((value, index) => (
          <React.Fragment key={index}>{renderThumb(value)}</React.Fragment>
        ))}
      </SliderPrimitive.Root>
    </div>
  );
});
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
